// Repo GitHub-identity binding (arc 101 slice 05) — which credential drives a
// repo's WORK. Exactly ONE mode per repo (the binding is exclusive), so intake,
// tools, and disclosure all key off the same value:
//   - `app`         the App installation (the default SaaS path; the bot acts as
//                   itself and always discloses).
//   - `user_token`  a specific user's connected fine-grained PAT — the repo is
//                   driven as the USER's own GitHub account, no App installed.
//
// The binding lives in `repo_bindings` (`packages/db`), read per repo by the tool
// wiring (`githubClientFor`) to pick the issuer, and per repo by the poller lane
// (a `user_token` repo has no App webhook — it is polled). `mode` is a text
// column, Zod-at-read (D3 no-pgEnum stance, same as `byok_keys.kind` /
// `user_github_tokens.token_kind`).

import { z } from 'zod';

/** The credential lanes a repo can be bound to. */
export const REPO_BINDING_MODES = ['app', 'user_token'] as const;
export type RepoBindingMode = (typeof REPO_BINDING_MODES)[number];
/** Text column, Zod-at-read — an unknown stored mode fails loud, never silently drifts identity. */
export const RepoBindingModeSchema = z.enum(REPO_BINDING_MODES);

// ── resolution: may a stored `user_token` binding drive its repo right now? ──
//
// A `repo_bindings` row names a user; the credential it acts through is that
// user's `fine_grained_pat` row (`user_github_tokens`), whose `repo_allowlist`
// is the set of repos the connect probe PROVED writable. A binding is usable only
// while that row exists, has not expired, and still lists the repo — the user can
// re-connect a narrower token, or let it lapse, without touching the binding. A
// binding that fails any of these must not resolve as `user_token`: minting the
// PAT for a repo it was never proven against is the confident-wrong-identity bug.

/** Why a `user_token` binding cannot drive its repo. Each is a different fix for the user. */
export const USER_TOKEN_GRANT_REFUSALS = [
  /**
   * The bound user no longer belongs to the repo's account (plan 101 04-7). A
   * `user_token` binding acts AS its user, so it is that member's reach, never the
   * org's: no PAT repair applies — an admin re-binds the repo or re-invites them.
   * Decided by the resolver's membership join (`resolveRepoBinding`), ahead of the
   * grant verdict below; `judgeUserTokenGrant` judges the token only.
   */
  'binder_not_member',
  /** The bound user has no connected fine-grained PAT (never connected, or disconnected). */
  'token_missing',
  /** The PAT's GitHub-reported expiry has passed — the user must connect a fresh one. */
  'token_expired',
  /**
   * The stored PAT row carries no expiry. The only writer (`POST /v1/me/github-token`)
   * refuses an unexpiring token, so this is a malformed row — refused, never trusted.
   */
  'token_missing_expiry',
  /** The PAT is live but its probed allowlist no longer names this repo. */
  'repo_not_in_allowlist',
] as const;
export type UserTokenGrantRefusal = (typeof USER_TOKEN_GRANT_REFUSALS)[number];

/** The stored facts of a user's connected PAT that decide what it may drive (never the token). */
export type UserTokenGrant = {
  expiresAt: Date | null;
  repoAllowlist: readonly string[] | null;
};

/**
 * Is `fullName` in the PAT's allowlist? GitHub owner and repo names are
 * case-insensitive (`Octo/Repo` and `octo/repo` are one repo), and the allowlist
 * keeps the casing the user typed while `repositories.full_name` keeps GitHub's,
 * so the comparison folds case. Nothing else is normalized: both sides are
 * already `RepoFullNameSchema`-shaped.
 */
export function repoInAllowlist(allowlist: readonly string[] | null, fullName: string): boolean {
  if (allowlist === null) return false;
  const want = fullName.toLowerCase();
  return allowlist.some((r) => r.toLowerCase() === want);
}

/**
 * The pure verdict: may the user's stored PAT (`grant`, null = none connected)
 * drive `repoFullName` at `now`? Checked in the order a user fixes them — connect,
 * then renew, then widen the token's repo scope.
 */
export function judgeUserTokenGrant(input: {
  grant: UserTokenGrant | null;
  repoFullName: string;
  now: Date;
}): { ok: true } | { ok: false; reason: UserTokenGrantRefusal } {
  const { grant, repoFullName, now } = input;
  if (grant === null) return { ok: false, reason: 'token_missing' };
  if (grant.expiresAt === null) return { ok: false, reason: 'token_missing_expiry' };
  if (grant.expiresAt.getTime() <= now.getTime()) return { ok: false, reason: 'token_expired' };
  if (!repoInAllowlist(grant.repoAllowlist, repoFullName)) {
    return { ok: false, reason: 'repo_not_in_allowlist' };
  }
  return { ok: true };
}

// ── bind: the user asks to drive one of their repos with their own PAT ──────
//
// ONE outcome type for both doors (`POST /v1/me/repo-bindings` and MCP
// `project add` with `via: 'user_token'`), produced by ONE executor in
// `apps/api` (`deps/user-token-binding.ts`) and rendered by ONE sentence table
// below, so the two surfaces cannot disagree about why a bind was refused.

/** Why a user-token bind was refused. */
export type UserTokenBindRefusal =
  | { kind: UserTokenGrantRefusal }
  /** The stored token is a classic `ghp_` PAT — custody blast radius, never used. */
  | { kind: 'classic_token' }
  /** GitHub 401'd the stored token: revoked or otherwise dead. */
  | { kind: 'token_rejected' }
  /** GitHub did not answer the live probe — retryable, nothing was written. */
  | { kind: 'probe_failed' }
  /** The live probe says the token may not push to the repo (today — the allowlist is a connect-time fact). */
  | { kind: 'repo_not_writable'; access: 'none' | 'read_only' }
  /** The account's tier may not operate a PRIVATE repo; `detail` is the billing rule's own sentence. */
  | { kind: 'private_repo_refused'; detail: string }
  /** A different account already operates this GitHub repo (the App lane, or another member's bind). */
  | { kind: 'repo_owned_by_other_account' };

export type UserTokenBindOutcome =
  | {
      kind: 'bound';
      repoId: string;
      /** GitHub's canonical `owner/name` (the probe's answer, not the caller's casing). */
      fullName: string;
      /** True when this bind created the `repositories` row (no App ever saw the repo). */
      created: boolean;
    }
  | { kind: 'refused'; refusal: UserTokenBindRefusal };

/** The human sentence for each refusal — the REST `detail` and the MCP error text. */
export function userTokenBindRefusalDetail(repo: string, refusal: UserTokenBindRefusal): string {
  switch (refusal.kind) {
    case 'binder_not_member':
      return `the user bound to ${repo} is no longer a member of this account — a current member must bind it again`;
    case 'token_missing':
      return 'no personal GitHub token is connected — connect a fine-grained token at /account/github first';
    case 'token_expired':
      return 'your connected GitHub token has expired — connect a fresh fine-grained token at /account/github';
    case 'token_missing_expiry':
      return 'your connected GitHub token has no recorded expiry — reconnect it at /account/github';
    case 'repo_not_in_allowlist':
      return `${repo} is not among the repositories your connected token was granted — reconnect the token with ${repo} included`;
    case 'classic_token':
      return 'the connected token is a classic (ghp_) token, which is never used — connect a fine-grained token';
    case 'token_rejected':
      return 'GitHub rejected your connected token (revoked or invalid) — connect a fresh one at /account/github';
    case 'probe_failed':
      return 'could not reach GitHub to confirm the token can write this repository; please try again';
    case 'repo_not_writable':
      return refusal.access === 'none'
        ? `your token can no longer access ${repo} — re-scope the fine-grained token to include it`
        : `your token can only read ${repo} — grant it "Contents: Read and write"`;
    case 'private_repo_refused':
      return refusal.detail;
    case 'repo_owned_by_other_account':
      return `${repo} is already operated by a different account`;
  }
}
